import { useCallback, useEffect, useState, type FormEvent } from "react";

import PasswordInputField from "@/components/feature/PasswordInputField";
import LegalAcceptance from "@/components/feature/LegalAcceptance";
import { legalAcceptanceMessage } from "@/shared/legal/documents";
import { fetchMemberPushConfiguration } from "@/features/public/api";
import { fetchAdminDashboard } from "@/features/admin/api";
import { AdminSessionProvider } from "@/features/admin/session";
import { useAdminSession } from "@/features/admin/useAdminSession";
import type { AdminDashboardData } from "@/features/admin/types";
import type { AdminUser } from "@/features/admin/types";
import { disableMemberPushNotifications, enableMemberPushNotifications, supportsMemberPushNotifications } from "@/shared/push/memberPushNotifications";
import { useRealtimeInvalidation } from "@/shared/realtime/useRealtimeInvalidation";
import { getPasswordValidationError, passwordRuleMessage } from "@/shared/validation/password";

import ActualitesTab from "./components/ActualitesTab";
import AdminSidebar from "./components/AdminSidebar";
import BureauTab from "./components/BureauTab";
import BoutiqueTab from "./components/BoutiqueTab";
import ClubTab from "./components/ClubTab";
import CommandesTab from "./components/CommandesTab";
import CotisationsTab from "./components/CotisationsTab";
import DashboardTab from "./components/DashboardTab";
import DirectionsTab from "./components/DirectionsTab";
import EquipesTab from "./components/EquipesTab";
import EvenementsTab from "./components/EvenementsTab";
import GalerieTab from "./components/GalerieTab";
import LivesTab from "./components/LivesTab";
import LoisirsTab from "./components/LoisirsTab";
import MembresTab from "./components/MembresTab";

type Tab = "dashboard" | "membres" | "cotisations" | "directions" | "bureau" | "equipes" | "actualites" | "loisirs" | "galerie" | "club" | "boutique" | "evenements" | "commandes" | "lives";

const ADMIN_REALTIME_TOPICS = [
  "dashboard",
  "members",
  "cotisations",
  "directions",
  "bureau",
  "equipes",
  "actualites",
  "loisirs",
  "galerie",
  "club",
  "boutique",
  "evenements",
  "commandes",
  "lives"
] as const;

const ADMIN_TOPIC_TO_TABS: Record<(typeof ADMIN_REALTIME_TOPICS)[number], Tab[]> = {
  dashboard: ["dashboard"],
  members: ["membres", "dashboard"],
  cotisations: ["cotisations", "membres", "dashboard"],
  directions: ["directions", "membres"],
  bureau: ["bureau", "dashboard"],
  equipes: ["equipes", "dashboard"],
  actualites: ["actualites", "dashboard"],
  loisirs: ["loisirs"],
  galerie: ["galerie"],
  club: ["club"],
  boutique: ["boutique", "dashboard"],
  evenements: ["evenements", "dashboard"],
  commandes: ["commandes", "dashboard"],
  lives: ["lives", "dashboard"]
};

function createTabRefreshState(): Record<Tab, number> {
  return {
    dashboard: 0,
    membres: 0,
    cotisations: 0,
    directions: 0,
    bureau: 0,
    equipes: 0,
    actualites: 0,
    loisirs: 0,
    galerie: 0,
    club: 0,
    boutique: 0,
    evenements: 0,
    commandes: 0,
    lives: 0
  };
}

function AdminLoginScreen() {
  const { login } = useAdminSession();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!acceptedTerms) { setError(legalAcceptanceMessage); return; }
    setError(null);
    setSubmitting(true);

    try {
      await login(identifier, password, acceptedTerms);
    } catch (caughtError) {
      const message = caughtError instanceof Error ? caughtError.message : "Connexion impossible";
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F7F5] flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md bg-white border border-orange-100 rounded-[28px] shadow-[0_24px_80px_rgba(232,82,26,0.08)] overflow-hidden">
        <div className="px-8 pt-8 pb-6 bg-[linear-gradient(135deg,#1A1A1A_0%,#2C2C2C_100%)]">
          <p className="text-xs uppercase tracking-[0.28em] text-[#E8521A] font-semibold">Administration</p>
          <h1 className="mt-3 text-2xl font-bold text-white">Connexion Admin ASC Sonatel</h1>
          <p className="mt-2 text-sm text-white/70">Authentifie-toi pour gérer les membres, contenus, produits et validations.</p>
        </div>

        <form onSubmit={handleSubmit} className="px-8 py-8 space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Email administrateur</label>
            <input
              type="text"
              value={identifier}
              onChange={(event) => setIdentifier(event.target.value)}
              placeholder="admin@asc-sonatel.sn"
              className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-[#E8521A] transition-colors"
              autoComplete="username"
            />
          </div>

          <PasswordInputField
            label="Mot de passe"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            placeholder="Votre mot de passe"
            className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-[#E8521A] transition-colors"
            autoComplete="current-password"
          />

          <LegalAcceptance id="admin-legal" checked={acceptedTerms} onChange={setAcceptedTerms} />
          {error && (
            <div className="px-4 py-3 rounded-xl bg-red-50 border border-red-100 text-sm text-red-600">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting || !acceptedTerms}
            className="w-full py-3 rounded-xl bg-[#E8521A] text-white text-sm font-semibold hover:bg-[#d44a16] disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
          >
            {submitting ? "Connexion..." : "Se connecter"}
          </button>
        </form>
      </div>
    </div>
  );
}

function isValidEmail(value: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

function AdminEmailModal({ user, onClose }: { user: AdminUser; onClose: () => void }) {
  const { updateProfile } = useAdminSession();
  const [email, setEmail] = useState(user.email ?? "");
  const [error, setError] = useState("");
  const [feedback, setFeedback] = useState<{ type: "success" | "error"; message: string } | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const normalizedEmail = email.trim().toLowerCase();

    if (!normalizedEmail) {
      setError("Merci de renseigner le nouvel email de connexion.");
      setFeedback(null);
      return;
    }

    if (!isValidEmail(normalizedEmail)) {
      setError("Merci de saisir une adresse email valide.");
      setFeedback(null);
      return;
    }

    setSubmitting(true);
    setError("");
    setFeedback(null);

    try {
      await updateProfile({
        firstName: user.prenom,
        lastName: user.nom,
        email: normalizedEmail
      });
      setEmail(normalizedEmail);
      setFeedback({
        type: "success",
        message: `Email de connexion mis à jour. La prochaine connexion se fera avec ${normalizedEmail}.`
      });
    } catch (caughtError) {
      setFeedback({
        type: "error",
        message: caughtError instanceof Error ? caughtError.message : "Impossible de modifier l'email de connexion."
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 py-6">
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-6 py-5">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#1A8C7A]">Compte admin</p>
            <h2 className="mt-2 text-xl font-bold text-[#1A1A1A]">Modifier l'email de connexion</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-10 w-10 items-center justify-center rounded-xl bg-gray-100 text-gray-500 transition hover:bg-gray-200 hover:text-gray-700"
            aria-label="Fermer"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5 px-6 py-6">
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">Email de connexion</label>
            <input
              type="email"
              value={email}
              onChange={(event) => {
                setEmail(event.target.value);
                setError("");
                setFeedback(null);
              }}
              className={`w-full rounded-xl border px-4 py-3 text-sm transition-colors focus:outline-none ${
                error ? "border-red-300 focus:border-red-500" : "border-gray-200 focus:border-[#1A8C7A]"
              }`}
              autoComplete="username"
              placeholder="admin@ascsonatel.com"
            />
            {error ? <p className="mt-2 text-sm text-red-600">{error}</p> : null}
          </div>

          {feedback ? (
            <div
              className={`rounded-xl border px-4 py-3 text-sm font-medium ${
                feedback.type === "success"
                  ? "border-emerald-100 bg-emerald-50 text-emerald-700"
                  : "border-red-100 bg-red-50 text-red-600"
              }`}
            >
              {feedback.message}
            </div>
          ) : null}

          <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-gray-200 px-4 py-3 text-sm font-semibold text-gray-600 transition hover:border-gray-300 hover:bg-gray-50"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#1A8C7A] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#157567] disabled:cursor-not-allowed disabled:opacity-60"
            >
              <i className={submitting ? "ri-loader-4-line animate-spin" : "ri-mail-settings-line"}></i>
              {submitting ? "Mise à jour..." : "Mettre à jour"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function AdminPasswordModal({ adminEmail, onClose }: { adminEmail: string | null; onClose: () => void }) {
  const { changePassword } = useAdminSession();
  const [form, setForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });
  const [errors, setErrors] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });
  const [feedback, setFeedback] = useState<{ type: "success" | "error"; message: string } | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const inputClassName = (hasError: boolean) =>
    `w-full rounded-xl border px-4 py-3 text-sm transition-colors focus:outline-none ${
      hasError ? "border-red-300 focus:border-red-500" : "border-gray-200 focus:border-[#E8521A]"
    }`;

  const updateField = (field: keyof typeof form, value: string) => {
    setForm((current) => ({
      ...current,
      [field]: value
    }));
    setErrors((current) => ({
      ...current,
      [field]: ""
    }));
    setFeedback(null);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const nextErrors = {
      currentPassword: "",
      newPassword: "",
      confirmPassword: ""
    };

    if (!form.currentPassword.trim()) {
      nextErrors.currentPassword = "Merci de renseigner le mot de passe actuel.";
    }

    nextErrors.newPassword = getPasswordValidationError(form.newPassword) ?? "";

    if (!form.confirmPassword.trim()) {
      nextErrors.confirmPassword = "Merci de confirmer le nouveau mot de passe.";
    } else if (form.newPassword !== form.confirmPassword) {
      nextErrors.confirmPassword = "La confirmation du nouveau mot de passe ne correspond pas.";
    }

    if (nextErrors.currentPassword || nextErrors.newPassword || nextErrors.confirmPassword) {
      setErrors(nextErrors);
      setFeedback({
        type: "error",
        message: "Corrige les champs du mot de passe en rouge."
      });
      return;
    }

    setSubmitting(true);
    setFeedback(null);

    try {
      await changePassword({
        currentPassword: form.currentPassword,
        newPassword: form.newPassword
      });
      setForm({
        currentPassword: "",
        newPassword: "",
        confirmPassword: ""
      });
      setErrors({
        currentPassword: "",
        newPassword: "",
        confirmPassword: ""
      });
      setFeedback({
        type: "success",
        message: `Mot de passe mis à jour. Le nouveau mot de passe a été envoyé à ${adminEmail ?? "l'email administrateur"}.`
      });
    } catch (caughtError) {
      setFeedback({
        type: "error",
        message: caughtError instanceof Error ? caughtError.message : "Impossible de modifier le mot de passe administrateur."
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 py-6">
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-6 py-5">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#E8521A]">Sécurité admin</p>
            <h2 className="mt-2 text-xl font-bold text-[#1A1A1A]">Changer le mot de passe</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-10 w-10 items-center justify-center rounded-xl bg-gray-100 text-gray-500 transition hover:bg-gray-200 hover:text-gray-700"
            aria-label="Fermer"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5 px-6 py-6">
          <PasswordInputField
            label="Mot de passe actuel"
            value={form.currentPassword}
            onChange={(event) => updateField("currentPassword", event.target.value)}
            className={inputClassName(Boolean(errors.currentPassword))}
            error={errors.currentPassword}
            autoComplete="current-password"
          />

          <div>
            <PasswordInputField
              label="Nouveau mot de passe"
              value={form.newPassword}
              onChange={(event) => updateField("newPassword", event.target.value)}
              className={inputClassName(Boolean(errors.newPassword))}
              error={errors.newPassword}
              autoComplete="new-password"
            />
            <p className="mt-2 text-xs text-gray-500">{passwordRuleMessage}</p>
          </div>

          <PasswordInputField
            label="Confirmer le nouveau mot de passe"
            value={form.confirmPassword}
            onChange={(event) => updateField("confirmPassword", event.target.value)}
            className={inputClassName(Boolean(errors.confirmPassword))}
            error={errors.confirmPassword}
            autoComplete="new-password"
          />

          {feedback ? (
            <div
              className={`rounded-xl border px-4 py-3 text-sm font-medium ${
                feedback.type === "success"
                  ? "border-emerald-100 bg-emerald-50 text-emerald-700"
                  : "border-red-100 bg-red-50 text-red-600"
              }`}
            >
              {feedback.message}
            </div>
          ) : null}

          <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-gray-200 px-4 py-3 text-sm font-semibold text-gray-600 transition hover:border-gray-300 hover:bg-gray-50"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#E8521A] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#d44a16] disabled:cursor-not-allowed disabled:opacity-60"
            >
              <i className={submitting ? "ri-loader-4-line animate-spin" : "ri-lock-password-line"}></i>
              {submitting ? "Mise à jour..." : "Mettre à jour"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function AdminPageContent() {
  const { accessToken, isReady, isAuthenticated, logout, request, user } = useAdminSession();
  const [activeTab, setActiveTab] = useState<Tab>("dashboard");
  const [pendingCount, setPendingCount] = useState(0);
  const [tabRefreshKeys, setTabRefreshKeys] = useState<Record<Tab, number>>(createTabRefreshState);
  const [adminPushEnabled, setAdminPushEnabled] = useState(false);
  const [adminPushRequesting, setAdminPushRequesting] = useState(false);
  const [adminPushError, setAdminPushError] = useState<string | null>(null);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const adminPushSupported = supportsMemberPushNotifications();

  const refreshPendingCount = useCallback(async () => {
    try {
      const dashboard = await fetchAdminDashboard(request);
      setPendingCount(dashboard.stats.adhesionsEnAttente);
    } catch {
      setPendingCount(0);
    }
  }, [request]);

  useEffect(() => {
    if (!isAuthenticated) {
      setPendingCount(0);
      return;
    }

    void refreshPendingCount();
  }, [isAuthenticated, refreshPendingCount]);

  const loadAdminPushConfiguration = useCallback(async () => {
    if (!accessToken || !adminPushSupported) {
      setAdminPushEnabled(false);
      setAdminPushError(null);
      return;
    }

    try {
      const config = await fetchMemberPushConfiguration(accessToken);
      setAdminPushEnabled(typeof Notification !== "undefined" && Notification.permission === "granted" && config.enabled);
      setAdminPushError(null);
    } catch (caughtError) {
      setAdminPushEnabled(false);
      setAdminPushError(caughtError instanceof Error ? caughtError.message : "Impossible de charger les notifications admin.");
    }
  }, [accessToken, adminPushSupported]);

  useEffect(() => {
    if (isAuthenticated) {
      void loadAdminPushConfiguration();
    }
  }, [isAuthenticated, loadAdminPushConfiguration]);

  const handleToggleAdminPush = useCallback(async () => {
    if (!accessToken) {
      return;
    }

    setAdminPushRequesting(true);
    setAdminPushError(null);

    try {
      if (adminPushEnabled) {
        await disableMemberPushNotifications(accessToken);
      } else {
        await enableMemberPushNotifications(accessToken);
      }

      await loadAdminPushConfiguration();
    } catch (caughtError) {
      setAdminPushError(caughtError instanceof Error ? caughtError.message : "Action push impossible.");
    } finally {
      setAdminPushRequesting(false);
    }
  }, [accessToken, adminPushEnabled, loadAdminPushConfiguration]);

  const handleDashboardLoaded = useCallback((dashboard: AdminDashboardData) => {
    setPendingCount(dashboard.stats.adhesionsEnAttente);
  }, []);

  useRealtimeInvalidation({
    eventName: "admin:data-invalidated",
    topics: [...ADMIN_REALTIME_TOPICS],
    accessToken,
    requiresAuth: true,
    enabled: isAuthenticated,
    onInvalidate: (payload) => {
      const affectedTabs = new Set<Tab>();

      payload.topics.forEach((topic) => {
        if (topic in ADMIN_TOPIC_TO_TABS) {
          ADMIN_TOPIC_TO_TABS[topic as keyof typeof ADMIN_TOPIC_TO_TABS].forEach((tab) => affectedTabs.add(tab));
        }
      });

      if (affectedTabs.size > 0) {
        setTabRefreshKeys((current) => {
          const next = { ...current };
          affectedTabs.forEach((tab) => {
            next[tab] += 1;
          });
          return next;
        });
      }

      if (payload.topics.includes("dashboard") || payload.topics.includes("members")) {
        void refreshPendingCount();
      }
    }
  });

  const renderTab = () => {
    switch (activeTab) {
      case "dashboard":
        return <DashboardTab key={`dashboard-${tabRefreshKeys.dashboard}`} onDashboardLoaded={handleDashboardLoaded} onOpenMembersTab={() => setActiveTab("membres")} />;
      case "membres":
        return <MembresTab key={`membres-${tabRefreshKeys.membres}`} onMembersChanged={refreshPendingCount} />;
      case "cotisations":
        return <CotisationsTab key={`cotisations-${tabRefreshKeys.cotisations}`} />;
      case "directions":
        return <DirectionsTab key={`directions-${tabRefreshKeys.directions}`} />;
      case "bureau":
        return <BureauTab key={`bureau-${tabRefreshKeys.bureau}`} />;
      case "equipes":
        return <EquipesTab key={`equipes-${tabRefreshKeys.equipes}`} />;
      case "lives":
        return <LivesTab key={`lives-${tabRefreshKeys.lives}`} />;
      case "actualites":
        return <ActualitesTab key={`actualites-${tabRefreshKeys.actualites}`} />;
      case "galerie":
        return <GalerieTab key={`galerie-${tabRefreshKeys.galerie}`} />;
      case "club":
        return <ClubTab key={`club-${tabRefreshKeys.club}`} />;
      case "loisirs":
        return <LoisirsTab key={`loisirs-${tabRefreshKeys.loisirs}`} />;
      case "boutique":
        return <BoutiqueTab key={`boutique-${tabRefreshKeys.boutique}`} />;
      case "evenements":
        return <EvenementsTab key={`evenements-${tabRefreshKeys.evenements}`} />;
      case "commandes":
        return <CommandesTab key={`commandes-${tabRefreshKeys.commandes}`} />;
      default:
        return <DashboardTab key={`dashboard-${tabRefreshKeys.dashboard}`} onDashboardLoaded={handleDashboardLoaded} onOpenMembersTab={() => setActiveTab("membres")} />;
    }
  };

  if (!isReady) {
    return (
      <div className="min-h-screen bg-[#F7F7F5] flex items-center justify-center">
        <div className="text-center">
          <div className="w-14 h-14 mx-auto rounded-2xl border-4 border-orange-100 border-t-[#E8521A] animate-spin"></div>
          <p className="mt-4 text-sm text-gray-500">Chargement de l&apos;espace administration...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    return <AdminLoginScreen />;
  }

  return (
    <div className="flex min-h-screen bg-[#F7F7F5] lg:h-screen lg:overflow-hidden">
      <AdminSidebar
        activeTab={activeTab}
        onTabChange={(tab) => setActiveTab(tab as Tab)}
        pendingCount={pendingCount}
        userName={`${user.prenom} ${user.nom}`}
        userEmail={user.email}
        onChangeEmail={() => setEmailModalOpen(true)}
        onChangePassword={() => setPasswordModalOpen(true)}
        onLogout={() => void logout()}
      />
      <main className="min-w-0 flex-1 overflow-auto pt-16 lg:pt-0">
        <div className="p-4 md:p-6 lg:p-8 max-w-[1200px]">
          {adminPushSupported ? (
            <div className="mb-4 flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:justify-end">
              {adminPushError ? <p className="text-xs font-medium text-red-600">{adminPushError}</p> : null}
              <button
                type="button"
                onClick={() => void handleToggleAdminPush()}
                disabled={adminPushRequesting}
                className={`inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-60 ${
                  adminPushEnabled
                    ? "border border-red-100 bg-red-50 text-red-600 hover:bg-red-100"
                    : "bg-[#1A8C7A] text-white hover:bg-[#157567]"
                }`}
              >
                <i className={adminPushRequesting ? "ri-loader-4-line animate-spin" : adminPushEnabled ? "ri-notification-off-line" : "ri-notification-3-line"}></i>
                {adminPushRequesting ? "Traitement..." : adminPushEnabled ? "Désactiver push admin" : "Activer push admin"}
              </button>
            </div>
          ) : null}
          {renderTab()}
        </div>
      </main>
      {emailModalOpen ? (
        <AdminEmailModal user={user} onClose={() => setEmailModalOpen(false)} />
      ) : null}
      {passwordModalOpen ? (
        <AdminPasswordModal adminEmail={user.email} onClose={() => setPasswordModalOpen(false)} />
      ) : null}
    </div>
  );
}

export default function AdminPage() {
  return (
    <AdminSessionProvider>
      <AdminPageContent />
    </AdminSessionProvider>
  );
}
